import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import { BugReport, Lightbulb, RateReview, Feedback, ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';

const FEEDBACK_TYPES = [
  { value: 'feedback', label: 'General', icon: <Feedback /> },
  { value: 'bug', label: 'Bug Report', icon: <BugReport /> },
  { value: 'feature', label: 'Feature Idea', icon: <Lightbulb /> },
  { value: 'review', label: 'Review', icon: <RateReview /> },
];

export default function FeedbackPage() {
  const navigate = useNavigate();
  const [feedbackType, setFeedbackType] = useState('feedback');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      setError('Please enter your feedback.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const functions = getFunctions(getApp(), 'us-central1');
      const submitFeedbackFn = httpsCallable(functions, 'submitFeedback');
      await submitFeedbackFn({
        type: feedbackType,
        subject: subject || feedbackType,
        message: message.trim(),
        email: email.trim(),
        userName: email || 'Anonymous',
        familyId: null,
      });
      setSuccess(true);
      setMessage('');
      setSubject('');
      setEmail('');
    } catch (err) {
      console.error('Feedback submit error:', err);
      setError('Failed to submit. Please try again or email support@earn-echore.app');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Navigation header with proper 48px tap targets */}
      <Box
        component="header"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          minHeight: 56,
        }}
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ minHeight: 48, minWidth: 48 }}
        >
          Back to Home
        </Button>
      </Box>
      <Container maxWidth="sm" sx={{ py: 4 }}>

        <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
          <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
            💬 Share Your Feedback
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Help us improve Earn-E! We read every message.
          </Typography>

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Thank you! Your feedback has been submitted. 🎉
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Typography variant="subtitle2" gutterBottom>
              Feedback Type
            </Typography>
            <ToggleButtonGroup
              value={feedbackType}
              exclusive
              onChange={(_, v) => v && setFeedbackType(v)}
              sx={{ mb: 3, flexWrap: 'wrap', gap: 1, width: '100%' }}
            >
              {FEEDBACK_TYPES.map((t) => (
                <ToggleButton
                  key={t.value}
                  value={t.value}
                  sx={{ gap: 1, flex: '1 1 auto', minHeight: 48, minWidth: 44 }}
                >
                  {t.icon}
                  {t.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <TextField
              label="Your Email (optional)"
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              label="Subject (optional)"
              fullWidth
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              label="Your Message"
              multiline
              rows={5}
              fullWidth
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={submitting || !message.trim()}
              startIcon={submitting ? <CircularProgress size={20} /> : <Feedback />}
              sx={{
                minHeight: 48,
                '&:disabled': {
                  opacity: 0.5,
                  cursor: 'not-allowed'
                }
              }}
            >
              {submitting ? 'Submitting...' : 'Submit Feedback'}
            </Button>
            {!message.trim() && !submitting && (
              <Typography variant="body2" sx={{ fontSize: '14px', color: '#666', mt: 1, textAlign: 'center' }}>
                ✓ Type your message above to enable submit
              </Typography>
            )}
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
            You can also email us directly at{' '}
            <a href="mailto:support@earn-echore.app">support@earn-echore.app</a>
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}
